import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import api, { setAuthToken } from "utils/api";
import { useToastContext } from "context/ToastContext";

const AuthContext = createContext();

const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAppLoading, setIsAppLoading] = useState(true);
  const [error, setError] = useState();
  const navigate = useNavigate();
  const { notify } = useToastContext();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setAuthToken(token);
      setIsLoggedIn(true);
    }
    // use this to handle auth pages loading
    setIsAppLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      setError(undefined);
      const response = await api.post("/login", { email, password });
      const { token, userData } = response.data;
      if (!!userData.roles.find((role) => role.name === "Admin")) {
        setAuthToken(token);
        setIsLoggedIn(true);
        localStorage.setItem("token", token);
        notify({ title: "Success", description: "You've successfully logged in!", type: "success" });
        navigate("/products");
      } else {
        setIsLoggedIn(false);
      }
    } catch (error) {
      setIsLoggedIn(false);
      setIsLoading(false);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      const response = await api.delete("/logout");
      setIsLoading(false);
      setIsLoggedIn(false);
      setAuthToken(null);
      localStorage.removeItem("token");
      notify({ type: "success", title: "Goodbye!", description: response.data?.message ?? "You have been logged out." });
    } catch (error) {
      notify({ type: "danger", title: "Whoops!", description: error.response?.data?.message ?? error.message });
      setIsLoading(false);
    } finally {
      navigate("/login");
    }
  };

  return <AuthContext.Provider value={{ isLoggedIn, login, logout, isLoading, error, isAppLoading }}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider, useAuthContext };
